import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createListsClient } from './api';
import type { ListsFilter, RewriteListMutationPayload, SchainListMutationPayload } from './model';

export const listsQueryKey = ['supply', 'lists'] as const;
export const allListsQueryKey = [...listsQueryKey, 'all'] as const;
export const rewriteListsQueryKey = [...listsQueryKey, 'rewrite', 'list'] as const;
export const schainListsQueryKey = [...listsQueryKey, 'schain', 'list'] as const;
export const schainDetailsQueryKey = [...listsQueryKey, 'schain', 'detail'] as const;

function useListsClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  return createListsClient({
    config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
    accessToken: session.accessToken,
  });
}

export function useAllListsQuery(filter: ListsFilter, enabled = true) {
  const client = useListsClient();
  return useQuery({
    queryKey: [...allListsQueryKey, filter],
    queryFn: () => client.listAll(filter),
    enabled,
  });
}

export function useRewriteListsQuery(filter: ListsFilter, enabled = true) {
  const client = useListsClient();
  return useQuery({
    queryKey: [...rewriteListsQueryKey, filter],
    queryFn: () => client.listRewrite(filter),
    enabled,
  });
}

export function useSchainListsQuery(filter: ListsFilter, enabled = true) {
  const client = useListsClient();
  return useQuery({
    queryKey: [...schainListsQueryKey, filter],
    queryFn: () => client.listSchain(filter),
    enabled,
  });
}

export function useRewriteListQuery(id: string | undefined) {
  const client = useListsClient();
  return useQuery({
    queryKey: [...listsQueryKey, 'rewrite', 'detail', id],
    queryFn: () => client.getRewrite(id ?? ''),
    enabled: Boolean(id),
    refetchOnMount: 'always',
  });
}

export function useSchainListQuery(id: string | undefined) {
  const client = useListsClient();
  return useQuery({
    queryKey: [...schainDetailsQueryKey, id],
    queryFn: () => client.getSchain(id ?? ''),
    enabled: Boolean(id),
    refetchOnMount: 'always',
  });
}

export function useListsMutations() {
  const client = useListsClient();
  const queryClient = useQueryClient();
  const invalidateRewriteLists = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: allListsQueryKey }),
      queryClient.invalidateQueries({ queryKey: rewriteListsQueryKey }),
    ]);
  const invalidateSchainLists = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: allListsQueryKey }),
      queryClient.invalidateQueries({ queryKey: schainListsQueryKey }),
    ]);
  return {
    createRewrite: useMutation({
      mutationFn: client.createRewrite,
      onSuccess: invalidateRewriteLists,
    }),
    updateRewrite: useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: RewriteListMutationPayload }) =>
        client.updateRewrite(id, payload),
      onSuccess: invalidateRewriteLists,
    }),
    deleteRewrite: useMutation({
      mutationFn: client.deleteRewrite,
      onSuccess: invalidateRewriteLists,
    }),
    createSchain: useMutation({
      mutationFn: client.createSchain,
      onSuccess: invalidateSchainLists,
    }),
    updateSchain: useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: SchainListMutationPayload }) =>
        client.updateSchain(id, payload),
      onSuccess: invalidateSchainLists,
    }),
    deleteSchain: useMutation({
      mutationFn: client.deleteSchain,
      onSuccess: invalidateSchainLists,
    }),
    importSchain: useMutation({
      mutationFn: ({ id, file }: { id: string; file: File }) => client.importSchain(id, file),
      onSuccess: (_data, variables) =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: allListsQueryKey }),
          queryClient.invalidateQueries({ queryKey: schainListsQueryKey }),
          queryClient.invalidateQueries({
            queryKey: [...schainDetailsQueryKey, variables.id],
          }),
        ]),
    }),
    // react-doctor-disable-next-line react-doctor/query-mutation-missing-invalidation -- read-only file export, writes no server state
    exportSchain: useMutation({ mutationFn: client.exportSchain }),
  };
}
